/**
 * IDP API Service
 * Handles all API calls for IDP configuration management
 */

import apiClient from '../../../assets/scripts/services/apiClient';

// Auth Settings
export const getAuthSettings = async (orgId) => {
  return apiClient.get(`/organizations/${orgId}/auth-settings`);
};

export const updateAuthSettings = async (orgId, settings) => {
  return apiClient.put(`/organizations/${orgId}/auth-settings`, settings);
};

// IDP Configurations
export const listIdpConfigurations = async (orgId, activeOnly = true) => {
  const query = activeOnly ? '?active_only=true' : '';
  return apiClient.get(`/organizations/${orgId}/idp-configurations${query}`);
};

export const getIdpConfiguration = async (orgId, idpId) => {
  return apiClient.get(`/organizations/${orgId}/idp-configurations/${idpId}`);
};

export const createIdpViaDiscovery = async (orgId, data) => {
  return apiClient.post(`/organizations/${orgId}/idp-configurations/discover`, data);
};

export const createIdpManual = async (orgId, data) => {
  return apiClient.post(`/organizations/${orgId}/idp-configurations`, data);
};

export const updateIdpConfiguration = async (orgId, idpId, data) => {
  return apiClient.put(`/organizations/${orgId}/idp-configurations/${idpId}`, data);
};

export const deleteIdpConfiguration = async (orgId, idpId) => {
  return apiClient.delete(`/organizations/${orgId}/idp-configurations/${idpId}`);
};

// Static Keys
export const uploadPublicKey = async (orgId, idpId, data) => {
  return apiClient.post(`/organizations/${orgId}/idp-configurations/${idpId}/keys`, data);
};

export const removePublicKey = async (orgId, idpId, kid) => {
  return apiClient.delete(`/organizations/${orgId}/idp-configurations/${idpId}/keys/${encodeURIComponent(kid)}`);
};

// Application IDP Access
export const getApplicationIdpAccess = async (orgId, appId) => {
  return apiClient.get(`/organizations/${orgId}/applications/${appId}/idp-access`);
};

export const grantIdpAccess = async (orgId, appId, idpConfigId) => {
  return apiClient.post(`/organizations/${orgId}/applications/${appId}/idp-access`, {
    idp_config_id: idpConfigId,
  });
};

export const revokeIdpAccess = async (orgId, appId, idpId) => {
  return apiClient.delete(`/organizations/${orgId}/applications/${appId}/idp-access/${idpId}`);
};

export const getIdpApplications = async (orgId, idpId) => {
  return apiClient.get(`/organizations/${orgId}/idp-configurations/${idpId}/applications`);
};

// User Identity
export const getUserIdentities = async (orgId, userId) => {
  return apiClient.get(`/organizations/${orgId}/users/${userId}/identities`);
};

export const linkUserIdentity = async (orgId, userId, data) => {
  return apiClient.post(`/organizations/${orgId}/users/${userId}/identities`, data);
};

export const unlinkUserIdentity = async (orgId, userId, linkId) => {
  return apiClient.delete(`/organizations/${orgId}/users/${userId}/identities/${linkId}`);
};

// Token Testing
export const getTokenInfo = async (orgId, token) => {
  return apiClient.post(`/organizations/${orgId}/auth/token-info`, { token });
};

// Auth Config (for test tab)
export const getAuthConfig = async (orgId) => {
  return apiClient.get(`/organizations/${orgId}/auth-config`);
};

// Verify token for an application
export const verifyAppToken = async (orgId, appId, token) => {
  return apiClient.post(`/organizations/${orgId}/applications/${appId}/auth/verify`, { token });
};

export default {
  getAuthSettings,
  updateAuthSettings,
  listIdpConfigurations,
  getIdpConfiguration,
  createIdpViaDiscovery,
  createIdpManual,
  updateIdpConfiguration,
  deleteIdpConfiguration,
  uploadPublicKey,
  removePublicKey,
  getApplicationIdpAccess,
  grantIdpAccess,
  revokeIdpAccess,
  getIdpApplications,
  getUserIdentities,
  linkUserIdentity,
  unlinkUserIdentity,
  getTokenInfo,
  getAuthConfig,
  verifyAppToken,
};
